import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import Sidebar from "@/components/Sidebar";
import axios from "axios";
import { toast } from "sonner";
import { 
  Headphones, 
  Plus, 
  Download, 
  Trash2, 
  Loader2, 
  BookOpen,
  Clock,
  Music,
  Volume2,
  ImageIcon,
  Upload,
  ArrowRight,
  ArrowLeft,
  Check,
  X,
  RefreshCw
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VOICE_OPTIONS = [
  { value: "nova", label: "Nova", description: "Energetic, upbeat" },
  { value: "alloy", label: "Alloy", description: "Neutral, balanced" },
  { value: "echo", label: "Echo", description: "Smooth, calm" },
  { value: "fable", label: "Fable", description: "Expressive, storytelling" },
  { value: "onyx", label: "Onyx", description: "Deep, authoritative" },
  { value: "shimmer", label: "Shimmer", description: "Bright, cheerful" },
  { value: "ash", label: "Ash", description: "Clear, articulate" },
  { value: "sage", label: "Sage", description: "Wise, measured" },
  { value: "coral", label: "Coral", description: "Warm, friendly" }
];

const AudiobookCreator = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [audiobooks, setAudiobooks] = useState([]);
  const [books, setBooks] = useState([]);
  const [covers, setCovers] = useState([]);
  const [userCredits, setUserCredits] = useState(0);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedAudiobook, setSelectedAudiobook] = useState(null);
  
  // Wizard state
  const [wizardStep, setWizardStep] = useState(1);
  const [sourceType, setSourceType] = useState("dtr_book");
  const [selectedBook, setSelectedBook] = useState("");
  const [externalContent, setExternalContent] = useState("");
  const [externalTitle, setExternalTitle] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Cover state
  const [coverSourceType, setCoverSourceType] = useState("none");
  const [selectedCover, setSelectedCover] = useState("none");
  const [uploadedCoverData, setUploadedCoverData] = useState(null);
  const [uploadedCoverName, setUploadedCoverName] = useState("");
  
  // Audio settings
  const [selectedVoice, setSelectedVoice] = useState("nova");
  const [selectedModel, setSelectedModel] = useState("tts-1");
  const [selectedSpeed, setSelectedSpeed] = useState("1.0");

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [audiobooksRes, booksRes, coversRes, userRes] = await Promise.all([
        axios.get(`${API}/audiobooks`),
        axios.get(`${API}/books`),
        axios.get(`${API}/covers`),
        axios.get(`${API}/auth/me`)
      ]);
      
      setAudiobooks(audiobooksRes.data.audiobooks || []);
      const booksData = Array.isArray(booksRes.data) ? booksRes.data : (booksRes.data.books || []);
      setBooks(booksData.filter(b => b.status === "completed"));
      const coversData = Array.isArray(coversRes.data) ? coversRes.data : (coversRes.data.projects || []);
      setCovers(coversData.filter(c => c.covers?.length > 0 && c.status === "completed"));
      setUserCredits(userRes.data.credits || 0);
    } catch (error) {
      console.error("Failed to load data:", error);
      // If authentication failed, redirect to login
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        toast.error("Session expired. Please log in again.");
        localStorage.removeItem("token");
        navigate("/");
        return;
      }
      toast.error("Failed to load data. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const resetWizard = () => {
    setWizardStep(1);
    setSourceType("dtr_book");
    setSelectedBook("");
    setExternalContent("");
    setExternalTitle("");
    setUploadedFileName("");
    setCoverSourceType("none");
    setSelectedCover("none");
    setUploadedCoverData(null);
    setUploadedCoverName("");
    setSelectedVoice("nova");
    setSelectedModel("tts-1");
    setSelectedSpeed("1.0");
  };

  const handleBookFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['.txt', '.docx', '.pdf'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(fileExt)) {
      toast.error("Please upload a .txt, .docx, or .pdf file");
      return;
    }
    
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File must be less than 20MB");
      return;
    }
    
    setUploadingFile(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/extract-book-text`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000 // 60 second timeout for large files
      });
      
      if (response.data.text) {
        setExternalContent(response.data.text);
        setUploadedFileName(file.name);
        // Auto-set title from filename if not already set
        if (!externalTitle) {
          const titleFromFile = file.name.replace(/\.[^/.]+$/, "").replace(/[_-]/g, " ");
          setExternalTitle(titleFromFile);
        }
        toast.success(`Extracted ${response.data.text.length.toLocaleString()} characters from ${file.name}`);
      } else {
        toast.error("Could not extract text from file");
      }
    } catch (error) {
      console.error("File upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to extract text from file");
    } finally {
      setUploadingFile(false);
    }
  };

  const handleCoverUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result?.toString().split(',')[1];
      setUploadedCoverData(base64);
      setUploadedCoverName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleCreate = async () => {
    if (sourceType === "dtr_book" && !selectedBook) {
      toast.error("Please select a book");
      return;
    }
    
    if (sourceType === "external" && (!externalContent.trim() || !externalTitle.trim())) {
      toast.error("Please enter a title and content for your book");
      return;
    }

    if (userCredits < 3) {
      toast.error("Insufficient credits. You need 3 credits to create an audiobook.");
      return;
    }

    setCreating(true);
    try {
      console.log("[Audiobook] Creating audiobook with params:", {
        source_type: sourceType,
        book_id: sourceType === "dtr_book" ? selectedBook : null,
        cover_id: coverSourceType === "dtr_cover" && selectedCover !== "none" ? selectedCover : null,
        voice: selectedVoice,
        model: selectedModel
      });
      
      const response = await axios.post(`${API}/audiobooks/create`, {
        source_type: sourceType,
        book_id: sourceType === "dtr_book" ? selectedBook : null,
        external_content: sourceType === "external" ? externalContent : null,
        external_title: sourceType === "external" ? externalTitle : null,
        cover_id: coverSourceType === "dtr_cover" && selectedCover !== "none" ? selectedCover : null,
        external_cover_data: coverSourceType === "external" ? uploadedCoverData : null,
        voice: selectedVoice,
        model: selectedModel,
        speed: parseFloat(selectedSpeed)
      }, {
        timeout: 30000 // 30 second timeout for initial request
      });
      
      console.log("[Audiobook] Creation response:", response.data);
      
      // Optimistically add the audiobook to the list so user sees it immediately
      const newAudiobook = {
        id: response.data.audiobook_id,
        title: sourceType === "dtr_book" 
          ? (books.find(b => b.id === selectedBook)?.title || "Generating...")
          : externalTitle || "Generating...",
        status: "generating",
        voice: selectedVoice,
        created_at: new Date().toISOString()
      };
      setAudiobooks(prev => [newAudiobook, ...prev]);
      
      toast.success("Audiobook generation started! This may take up to 5 hours for longer books. You can close this page and check back later.");
      setShowNewDialog(false);
      resetWizard();
      
      // Start polling for status
      pollAudiobookStatus(response.data.audiobook_id);
    } catch (error) {
      console.error("[Audiobook] Failed to create audiobook:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to create audiobook";
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const pollAudiobookStatus = async (audiobookId) => {
    let pollCount = 0;
    const maxPolls = 180; // Max 30 minutes of polling (180 * 10 seconds)
    let consecutiveErrors = 0;
    
    const checkStatus = async () => {
      pollCount++;
      console.log(`[Audiobook] Polling status for ${audiobookId}, attempt ${pollCount}`);
      
      try {
        const response = await axios.get(`${API}/audiobooks/${audiobookId}`);
        consecutiveErrors = 0; // Reset on success
        console.log(`[Audiobook] Status: ${response.data.status}`);
        
        // Update the audiobook in the list with latest status
        setAudiobooks(prev => prev.map(ab => 
          ab.id === audiobookId ? { ...ab, ...response.data } : ab
        ));
        
        if (response.data.status === "completed") {
          toast.success("🎧 Audiobook is ready for download!");
        } else if (response.data.status === "error") {
          toast.error("Audiobook generation failed: " + (response.data.error_message || "Unknown error"));
        } else if (pollCount >= maxPolls) {
          toast.error("Audiobook generation is taking too long. Please check back later.");
        } else {
          // Still generating, continue polling
          setTimeout(checkStatus, 10000);
        }
      } catch (error) {
        console.error("[Audiobook] Error checking status:", error);
        consecutiveErrors++;
        
        // If auth error, stop polling
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          toast.error("Session expired. Please log in again to check audiobook status.");
          return;
        }
        
        // If audiobook not found (maybe deleted)
        if (error?.response?.status === 404) {
          toast.error("Audiobook not found. It may have been deleted.");
          setAudiobooks(prev => prev.filter(ab => ab.id !== audiobookId));
          return;
        }
        
        // Allow a few consecutive errors before giving up (network issues)
        if (consecutiveErrors >= 3) {
          toast.error("Lost connection while checking audiobook status. Please refresh the page.");
          return;
        }
        
        // Retry after error
        setTimeout(checkStatus, 10000);
      }
    };
    
    // Start polling after 5 seconds
    setTimeout(checkStatus, 5000);
  };

  const [showChapterDownload, setShowChapterDownload] = useState(false);
  const [chapterList, setChapterList] = useState([]);
  const [downloadingChapter, setDownloadingChapter] = useState(null);

  const handleDownload = async (audiobook) => {
    // Check file size first - if over 100MB, show chapter download dialog
    const fileSizeMB = (audiobook.file_size_bytes || 0) / (1024 * 1024);
    
    if (fileSizeMB > 100) {
      // Large file - need to download by chapter
      toast.info(`This audiobook is ${Math.round(fileSizeMB)}MB. Loading chapter list for download...`);
      try {
        const response = await axios.get(`${API}/audiobooks/${audiobook.id}/chapters`);
        console.log("[Audiobook] Chapters response:", response.data);
        const chapters = response.data.chapters || [];
        if (chapters.length === 0) {
          toast.error("No download parts available. The audiobook may not have been saved correctly.");
          return;
        }
        setChapterList(chapters);
        setSelectedAudiobook(audiobook);
        setShowChapterDownload(true);
      } catch (error) {
        console.error("[Audiobook] Failed to load chapters:", error);
        const errorMsg = error.response?.data?.detail || error.message || "Unknown error";
        toast.error(`Failed to load chapter list: ${errorMsg}`);
      }
      return;
    }
    
    // Small file - download normally
    try {
      toast.loading("Preparing download...", { id: "download" });
      const response = await axios.get(`${API}/audiobooks/${audiobook.id}/download`, {
        responseType: 'blob',
        timeout: 300000,
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            toast.loading(`Downloading... ${percent}%`, { id: "download" });
          }
        }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const safeTitle = audiobook.title.replace(/[^a-zA-Z0-9\s-_]/g, '').substring(0, 50);
      link.setAttribute('download', `${safeTitle}.mp3`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Download complete!", { id: "download" });
    } catch (error) {
      console.error("Download error:", error);
      if (error.response?.status === 400 && error.response?.data?.detail?.includes("too large")) {
        // File too large - show chapter download
        toast.info("File too large for single download. Loading chapters...", { id: "download" });
        try {
          const response = await axios.get(`${API}/audiobooks/${audiobook.id}/chapters`);
          setChapterList(response.data.chapters || []);
          setSelectedAudiobook(audiobook);
          setShowChapterDownload(true);
        } catch (err) {
          toast.error("Failed to load chapter list", { id: "download" });
        }
      } else {
        toast.error(`Download failed: ${error.response?.data?.detail || error.message}`, { id: "download" });
      }
    }
  };

  const handleDownloadChapter = async (audiobook, chapterNum) => {
    setDownloadingChapter(chapterNum);
    try {
      toast.loading(`Downloading Chapter ${chapterNum}...`, { id: `download-ch-${chapterNum}` });
      const response = await axios.get(`${API}/audiobooks/${audiobook.id}/download/${chapterNum}`, {
        responseType: 'blob',
        timeout: 120000,
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            toast.loading(`Chapter ${chapterNum}: ${percent}%`, { id: `download-ch-${chapterNum}` });
          }
        }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const safeTitle = audiobook.title.replace(/[^a-zA-Z0-9\s-_]/g, '').substring(0, 40);
      link.setAttribute('download', `${safeTitle}_chapter_${chapterNum}.mp3`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Chapter ${chapterNum} downloaded!`, { id: `download-ch-${chapterNum}` });
    } catch (error) {
      toast.error(`Failed to download Chapter ${chapterNum}`, { id: `download-ch-${chapterNum}` });
    } finally {
      setDownloadingChapter(null);
    }
  };

  const handleDownloadAllChapters = async (audiobook) => {
    toast.info("Downloading all chapters... This may take a while.");
    for (let i = 0; i < chapterList.length; i++) {
      await handleDownloadChapter(audiobook, chapterList[i].chapter_number);
      // Small delay between downloads
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    toast.success("All chapters downloaded!");
  };

  const handleDelete = async (audiobookId) => {
    if (!confirm("Are you sure you want to delete this audiobook?")) return;
    
    try {
      await axios.delete(`${API}/audiobooks/${audiobookId}`);
      toast.success("Audiobook deleted");
      loadData();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete audiobook");
    }
  };

  const handleForceStop = async (audiobookId) => {
    if (!confirm("Are you sure you want to stop this audiobook generation? Any progress will be lost.")) return;
    
    try {
      await axios.post(`${API}/audiobooks/${audiobookId}/force-stop`);
      toast.success("Audiobook generation stopped");
      loadData();
    } catch (error) {
      console.error("Force stop error:", error);
      toast.error(error.response?.data?.detail || "Failed to stop audiobook");
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "Unknown";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes} min`;
  };

  const canProceedStep1 = sourceType !== "";
  const canProceedStep2 = sourceType === "dtr_book" ? selectedBook !== "" : (externalContent.trim() !== "" && externalTitle.trim() !== "");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-stone-50 to-neutral-100">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-lg font-medium text-stone-700">Loading Audiobook Creator...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-stone-50 to-neutral-100">
      <Sidebar onLogout={handleLogout} />
      
      <div className="ml-0 md:ml-64 p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold playfair text-stone-900">Audiobook Creator</h1>
              <p className="text-stone-600 mt-1">Transform your books into professional audiobooks</p>
            </div>
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="px-4 py-2 text-lg">
                {userCredits} Credits
              </Badge>
              <Button 
                onClick={() => {
                  resetWizard();
                  setShowNewDialog(true);
                }} 
                size="lg"
                className="bg-purple-700 hover:bg-purple-600 text-white rounded-full shadow-lg"
              >
                <Plus className="h-5 w-5 mr-2" />
                New Audiobook
              </Button>
              <Button 
                onClick={async () => {
                  try {
                    const response = await axios.post(`${API}/audiobooks/check-stuck`);
                    if (response.data.marked_as_error > 0) {
                      toast.success(`Marked ${response.data.marked_as_error} stuck audiobook(s) as failed`);
                      loadData();
                    } else {
                      toast.info("No stuck audiobooks found");
                    }
                  } catch (error) {
                    toast.error("Failed to check stuck audiobooks");
                  }
                }} 
                size="lg"
                variant="outline"
                className="rounded-full shadow-lg"
              >
                <RefreshCw className="h-5 w-5 mr-2" />
                Check Stuck
              </Button>
            </div>
          </div>

          {/* Info Card */}
          <Card className="mb-8 border-purple-200 bg-purple-50/50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-purple-100 rounded-full">
                  <Headphones className="h-6 w-6 text-purple-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-900 mb-1">Create Professional Audiobooks</h3>
                  <p className="text-stone-600 text-sm">
                    Convert your completed books or external content into audiobooks with expressive AI voices. 
                    Choose from 9 different voices and add your book cover as album art.
                  </p>
                  <div className="flex items-center gap-4 mt-3 text-sm text-purple-700">
                    <span className="flex items-center gap-1">
                      <Music className="h-4 w-4" />
                      Standard Audio Quality
                    </span>
                    <span className="flex items-center gap-1">
                      <Volume2 className="h-4 w-4" />
                      9 Voice Options
                    </span>
                    <span className="flex items-center gap-1">
                      <ImageIcon className="h-4 w-4" />
                      Cover Art Support
                    </span>
                  </div>
                  <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 text-sm font-medium">
                      💰 Cost: 3 Credits per audiobook
                    </p>
                  </div>
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-amber-800 text-sm flex items-start gap-2">
                      <Clock className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong>Generation Time:</strong> Audiobook creation may take up to 5 hours depending on book length. 
                        Average speed is approximately 8 chapters per hour.
                        <br/><br/>
                        <strong>⚠️ Important:</strong> Please <strong>keep this page open</strong> during generation. 
                        The preview server may go to sleep if inactive, which will interrupt the process.
                        <br/><br/>
                        <strong>💡 Pro Tip:</strong> Refresh the page to get updated progress. Use the "Check Stuck" button if generation seems stalled.
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Audiobooks Grid */}
          {audiobooks.length === 0 ? (
            <Card className="max-w-md mx-auto mt-12 border-stone-200 shadow-lg">
              <CardContent className="pt-12 pb-12 text-center">
                <Headphones className="h-16 w-16 text-stone-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold playfair text-stone-900 mb-2">No audiobooks yet</h3>
                <p className="text-stone-600 mb-6">
                  Create your first audiobook from a DTR book or external content
                </p>
                <Button 
                  onClick={() => {
                    resetWizard();
                    setShowNewDialog(true);
                  }}
                  className="bg-purple-700 hover:bg-purple-600 text-white rounded-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Audiobook
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {audiobooks.map((audiobook) => (
                <Card key={audiobook.id} className="border-stone-200 shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="h-2 bg-gradient-to-r from-purple-600 to-purple-400"></div>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg playfair text-stone-900 line-clamp-2">
                        {audiobook.title}
                      </CardTitle>
                      <Badge 
                        className={
                          audiobook.status === "completed" 
                            ? "bg-green-100 text-green-800" 
                            : audiobook.status === "generating"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-red-100 text-red-800"
                        }
                      >
                        {audiobook.status === "generating" && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                        {audiobook.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center gap-4 text-sm text-stone-600">
                        <span className="flex items-center gap-1">
                          <Volume2 className="h-4 w-4" />
                          {audiobook.voice}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {formatDuration(audiobook.duration_seconds)}
                        </span>
                      </div>
                      <div className="text-xs text-stone-500">
                        {audiobook.chapter_count} chapters • Created {new Date(audiobook.created_at).toLocaleDateString()}
                      </div>
                      
                      {audiobook.status === "generating" && (
                        <div className="space-y-2">
                          <Progress value={50} className="h-2" />
                          <p className="text-xs text-stone-500 text-center">
                            {audiobook.progress || "Starting generation..."}
                          </p>
                          <Button 
                            onClick={() => handleForceStop(audiobook.id)}
                            variant="outline"
                            size="sm"
                            className="w-full text-amber-600 border-amber-200 hover:bg-amber-50"
                          >
                            <X className="h-4 w-4 mr-1" />
                            Force Stop
                          </Button>
                        </div>
                      )}
                      
                      {audiobook.status === "error" && audiobook.error_message && (
                        <div className="bg-red-50 text-red-700 text-xs p-2 rounded border border-red-200">
                          Error: {audiobook.error_message}
                        </div>
                      )}
                      
                      <div className="flex gap-2 pt-2">
                        {audiobook.status === "completed" && (
                          <Button 
                            onClick={() => handleDownload(audiobook)}
                            className="flex-1 bg-purple-700 hover:bg-purple-600 text-white"
                            size="sm"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download MP3
                          </Button>
                        )}
                        <Button 
                          onClick={() => handleDelete(audiobook.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Audiobook Dialog - Multi-step Wizard */}
      <Dialog open={showNewDialog} onOpenChange={(open) => {
        setShowNewDialog(open);
        if (!open) resetWizard();
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl playfair">Create New Audiobook</DialogTitle>
            <DialogDescription>
              Step {wizardStep} of 4
            </DialogDescription>
          </DialogHeader>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-6">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  wizardStep >= step 
                    ? "bg-purple-600 text-white" 
                    : "bg-stone-200 text-stone-500"
                }`}>
                  {wizardStep > step ? <Check className="h-4 w-4" /> : step}
                </div>
                {step < 4 && (
                  <div className={`w-12 h-1 mx-1 ${
                    wizardStep > step ? "bg-purple-600" : "bg-stone-200"
                  }`} />
                )}
              </div>
            ))}
          </div>
          
          {/* Step 1: Choose Source Type */}
          {wizardStep === 1 && (
            <div className="space-y-6 py-4">
              <div>
                <Label className="text-base font-semibold">Choose Your Book Source</Label>
                <p className="text-sm text-stone-500 mt-1">Where is your book content coming from?</p>
              </div>
              
              <RadioGroup value={sourceType} onValueChange={setSourceType} className="space-y-3">
                <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  sourceType === "dtr_book" ? "border-purple-500 bg-purple-50" : "border-stone-200 hover:border-purple-300"
                }`} onClick={() => setSourceType("dtr_book")}>
                  <RadioGroupItem value="dtr_book" id="dtr_book" />
                  <div className="flex-1">
                    <Label htmlFor="dtr_book" className="text-base font-medium cursor-pointer">DTR Book</Label>
                    <p className="text-sm text-stone-500">Select from your completed books on the platform</p>
                  </div>
                  <BookOpen className="h-6 w-6 text-purple-600" />
                </div>
                
                <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  sourceType === "external" ? "border-purple-500 bg-purple-50" : "border-stone-200 hover:border-purple-300"
                }`} onClick={() => setSourceType("external")}>
                  <RadioGroupItem value="external" id="external" />
                  <div className="flex-1">
                    <Label htmlFor="external" className="text-base font-medium cursor-pointer">External Book</Label>
                    <p className="text-sm text-stone-500">Paste or upload content from outside the platform</p>
                  </div>
                  <Upload className="h-6 w-6 text-purple-600" />
                </div>
              </RadioGroup>

              <div className="flex justify-end pt-4">
                <Button 
                  onClick={() => setWizardStep(2)}
                  disabled={!canProceedStep1}
                  className="bg-purple-700 hover:bg-purple-600 text-white"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Select/Enter Book Content */}
          {wizardStep === 2 && (
            <div className="space-y-6 py-4">
              {sourceType === "dtr_book" ? (
                <>
                  <div>
                    <Label className="text-base font-semibold">Select Your Book</Label>
                    <p className="text-sm text-stone-500 mt-1">Choose a completed book from your dashboard</p>
                  </div>
                  
                  {books.length === 0 ? (
                    <div className="text-center py-8 bg-stone-50 rounded-lg">
                      <BookOpen className="h-12 w-12 text-stone-400 mx-auto mb-3" />
                      <p className="text-stone-600">No completed books found</p>
                      <p className="text-sm text-stone-500 mt-1">Complete a book first or use external content</p>
                    </div>
                  ) : (
                    <Select value={selectedBook} onValueChange={setSelectedBook}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Choose a completed book" />
                      </SelectTrigger>
                      <SelectContent>
                        {books.map((book) => (
                          <SelectItem key={book.id} value={book.id}>
                            <div className="flex items-center gap-2">
                              <BookOpen className="h-4 w-4" />
                              {book.title}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <Label className="text-base font-semibold">Upload or Enter Your Book</Label>
                    <p className="text-sm text-stone-500 mt-1">Upload a file or paste your book text</p>
                  </div>
                  
                  <div className="space-y-4">
                    {/* File Upload Section */}
                    <div>
                      <Label>Upload Book File</Label>
                      {uploadedFileName ? (
                        <div className="flex items-center justify-between p-3 mt-1 bg-purple-50 border border-purple-200 rounded-lg">
                          <div className="flex items-center gap-2">
                            <BookOpen className="h-5 w-5 text-purple-600" />
                            <span className="text-sm font-medium text-purple-800">{uploadedFileName}</span>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setUploadedFileName("");
                              setExternalContent("");
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-stone-300 rounded-lg p-4 mt-1 text-center hover:border-purple-400 transition-colors">
                          <input
                            type="file"
                            accept=".txt,.docx,.pdf"
                            onChange={handleBookFileUpload}
                            className="hidden"
                            id="book-upload"
                            disabled={uploadingFile}
                          />
                          <label htmlFor="book-upload" className="cursor-pointer">
                            {uploadingFile ? (
                              <>
                                <Loader2 className="h-8 w-8 text-purple-500 mx-auto mb-2 animate-spin" />
                                <p className="text-sm text-purple-600">Extracting text...</p>
                              </>
                            ) : (
                              <>
                                <Upload className="h-8 w-8 text-stone-400 mx-auto mb-2" />
                                <p className="text-sm text-stone-600">Click to upload book file</p>
                                <p className="text-xs text-stone-500 mt-1">.txt, .docx, or .pdf up to 20MB</p>
                              </>
                            )}
                          </label>
                        </div>
                      )}
                    </div>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-stone-300" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-stone-500">Or paste content</span>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="title">Book Title *</Label>
                      <Input
                        id="title"
                        value={externalTitle}
                        onChange={(e) => setExternalTitle(e.target.value)}
                        placeholder="Enter the title of your book"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="content">Book Content *</Label>
                      <Textarea
                        id="content"
                        value={externalContent}
                        onChange={(e) => setExternalContent(e.target.value)}
                        placeholder="Paste your book content here... (chapters will be detected by 'Chapter' headings)"
                        className="mt-1 min-h-[150px]"
                      />
                      <p className="text-xs text-stone-500 mt-1">
                        {externalContent.length.toLocaleString()} characters
                      </p>
                    </div>
                  </div>
                </>
              )}

              <div className="flex justify-between pt-4">
                <Button 
                  variant="outline"
                  onClick={() => setWizardStep(1)}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button 
                  onClick={() => setWizardStep(3)}
                  disabled={!canProceedStep2}
                  className="bg-purple-700 hover:bg-purple-600 text-white"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Select Cover Art */}
          {wizardStep === 3 && (
            <div className="space-y-6 py-4">
              <div>
                <Label className="text-base font-semibold">Select Cover Art (Optional)</Label>
                <p className="text-sm text-stone-500 mt-1">Add album artwork to your audiobook</p>
              </div>
              
              {/* Cover Source Selection */}
              <RadioGroup value={coverSourceType} onValueChange={(value) => {
                setCoverSourceType(value);
                if (value !== "dtr_cover") setSelectedCover("none");
                if (value !== "external") {
                  setUploadedCoverData(null);
                  setUploadedCoverName("");
                }
              }} className="space-y-3">
                <div className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  coverSourceType === "none" ? "border-purple-500 bg-purple-50" : "border-stone-200 hover:border-purple-300"
                }`} onClick={() => setCoverSourceType("none")}>
                  <RadioGroupItem value="none" id="cover_none" />
                  <div className="flex-1">
                    <Label htmlFor="cover_none" className="text-sm font-medium cursor-pointer">No Cover</Label>
                    <p className="text-xs text-stone-500">Continue without cover art</p>
                  </div>
                </div>
                
                <div className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  coverSourceType === "dtr_cover" ? "border-purple-500 bg-purple-50" : "border-stone-200 hover:border-purple-300"
                }`} onClick={() => setCoverSourceType("dtr_cover")}>
                  <RadioGroupItem value="dtr_cover" id="dtr_cover" />
                  <div className="flex-1">
                    <Label htmlFor="dtr_cover" className="text-sm font-medium cursor-pointer">DTR Cover</Label>
                    <p className="text-xs text-stone-500">Select from your created covers</p>
                  </div>
                  <ImageIcon className="h-5 w-5 text-purple-600" />
                </div>
                
                <div className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  coverSourceType === "external" ? "border-purple-500 bg-purple-50" : "border-stone-200 hover:border-purple-300"
                }`} onClick={() => setCoverSourceType("external")}>
                  <RadioGroupItem value="external" id="cover_external" />
                  <div className="flex-1">
                    <Label htmlFor="cover_external" className="text-sm font-medium cursor-pointer">Upload Cover</Label>
                    <p className="text-xs text-stone-500">Upload your own cover image</p>
                  </div>
                  <Upload className="h-5 w-5 text-purple-600" />
                </div>
              </RadioGroup>

              {/* DTR Cover Selection */}
              {coverSourceType === "dtr_cover" && (
                <div className="space-y-2">
                  {covers.length === 0 ? (
                    <div className="text-center py-6 bg-stone-50 rounded-lg">
                      <ImageIcon className="h-10 w-10 text-stone-400 mx-auto mb-2" />
                      <p className="text-sm text-stone-600">No covers available</p>
                      <p className="text-xs text-stone-500">Create covers in Cover Creation first</p>
                    </div>
                  ) : (
                    <Select value={selectedCover} onValueChange={setSelectedCover}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Choose a cover" />
                      </SelectTrigger>
                      <SelectContent>
                        {covers.map((cover) => (
                          <SelectItem key={cover.id} value={cover.id}>
                            <div className="flex items-center gap-2">
                              <ImageIcon className="h-4 w-4" />
                              {cover.title}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {/* External Cover Upload */}
              {coverSourceType === "external" && (
                <div className="space-y-2">
                  {uploadedCoverData ? (
                    <div className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <ImageIcon className="h-5 w-5 text-purple-600" />
                        <span className="text-sm font-medium text-purple-800">{uploadedCoverName}</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setUploadedCoverData(null);
                          setUploadedCoverName("");
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-stone-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleCoverUpload}
                        className="hidden"
                        id="cover-upload"
                      />
                      <label htmlFor="cover-upload" className="cursor-pointer">
                        <Upload className="h-8 w-8 text-stone-400 mx-auto mb-2" />
                        <p className="text-sm text-stone-600">Click to upload cover image</p>
                        <p className="text-xs text-stone-500 mt-1">PNG, JPG up to 5MB</p>
                      </label>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-between pt-4">
                <Button 
                  variant="outline"
                  onClick={() => setWizardStep(2)}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button 
                  onClick={() => setWizardStep(4)}
                  className="bg-purple-700 hover:bg-purple-600 text-white"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Voice Settings & Create */}
          {wizardStep === 4 && (
            <div className="space-y-6 py-4">
              <div>
                <Label className="text-base font-semibold">Voice & Audio Settings</Label>
                <p className="text-sm text-stone-500 mt-1">Customize how your audiobook sounds</p>
              </div>

              {/* Voice Selection */}
              <div className="space-y-2">
                <Label>Voice</Label>
                <Select value={selectedVoice} onValueChange={setSelectedVoice}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VOICE_OPTIONS.map((voice) => (
                      <SelectItem key={voice.value} value={voice.value}>
                        <div className="flex flex-col">
                          <span className="font-medium">{voice.label}</span>
                          <span className="text-xs text-stone-500">{voice.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Model Selection */}
              <div className="space-y-2">
                <Label>Audio Quality</Label>
                <Select value={selectedModel} onValueChange={setSelectedModel}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tts-1">Standard Quality (recommended)</SelectItem>
                    <SelectItem value="tts-1-hd">HD Quality (larger files)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Speed Selection */}
              <div className="space-y-2">
                <Label>Reading Speed</Label>
                <Select value={selectedSpeed} onValueChange={setSelectedSpeed}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.75">Slower (0.75x)</SelectItem>
                    <SelectItem value="1.0">Normal (1.0x)</SelectItem>
                    <SelectItem value="1.25">Faster (1.25x)</SelectItem>
                    <SelectItem value="1.5">Fast (1.5x)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Summary & Credit Info */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-2">
                <h4 className="font-medium text-stone-900">Summary</h4>
                <div className="text-sm text-stone-600 space-y-1">
                  <div>Source: {sourceType === "dtr_book" ? "DTR Book" : "External Content"}</div>
                  <div>Book: {sourceType === "dtr_book" 
                    ? books.find(b => b.id === selectedBook)?.title || "Not selected"
                    : externalTitle || "Not entered"
                  }</div>
                  <div>Cover: {
                    coverSourceType === "none" ? "None" :
                    coverSourceType === "dtr_cover" ? (covers.find(c => c.id === selectedCover)?.title || "Not selected") :
                    uploadedCoverName || "Not uploaded"
                  }</div>
                  <div>Voice: {VOICE_OPTIONS.find(v => v.value === selectedVoice)?.label}</div>
                </div>
                <div className="border-t border-purple-200 pt-2 mt-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Cost:</span>
                    <span className="font-semibold text-purple-700">3 Credits</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Your balance:</span>
                    <span className={userCredits >= 3 ? "text-green-600" : "text-red-600"}>
                      {userCredits} Credits
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <Button 
                  variant="outline"
                  onClick={() => setWizardStep(3)}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button 
                  onClick={handleCreate}
                  disabled={creating || userCredits < 3}
                  className="bg-purple-700 hover:bg-purple-600 text-white"
                >
                  {creating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Headphones className="h-4 w-4 mr-2" />
                      Create Audiobook
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Chapter Download Dialog */}
      <Dialog open={showChapterDownload} onOpenChange={setShowChapterDownload}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl playfair">Download by Chapter</DialogTitle>
            <DialogDescription>
              This audiobook is too large for a single download. Please download chapters individually.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {selectedAudiobook && (
              <div className="text-sm text-stone-600 mb-4">
                <strong>{selectedAudiobook.title}</strong>
                <br />
                Size: {Math.round((selectedAudiobook.file_size_bytes || 0) / (1024 * 1024))} MB
                <br />
                Chapters: {chapterList.length}
              </div>
            )}
            
            <Button 
              onClick={() => selectedAudiobook && handleDownloadAllChapters(selectedAudiobook)}
              className="w-full bg-amber-600 hover:bg-amber-700"
              disabled={downloadingChapter !== null}
            >
              <Download className="h-4 w-4 mr-2" />
              Download All Chapters (One by One)
            </Button>
            
            <div className="border-t pt-4">
              <p className="text-sm text-stone-500 mb-3">Or download individual chapters:</p>
              <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                {chapterList.map((chapter) => (
                  <Button
                    key={chapter.chapter_number}
                    variant="outline"
                    size="sm"
                    onClick={() => selectedAudiobook && handleDownloadChapter(selectedAudiobook, chapter.chapter_number)}
                    disabled={downloadingChapter === chapter.chapter_number}
                    className="text-xs"
                  >
                    {downloadingChapter === chapter.chapter_number ? (
                      <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <Download className="h-3 w-3 mr-1" />
                    )}
                    Ch {chapter.chapter_number}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AudiobookCreator;
